import React, { useState } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { faqs } from '../data/mockData';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real app, this would send the message
    alert('Thank you for contacting us! We will respond within 24 hours.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Page Header */}
        <div className="text-center mb-16">
          <h1 className="font-serif text-4xl md:text-6xl text-charcoal mb-4" data-testid="contact-page-title">
            Get in Touch
          </h1>
          <p className="text-[#6B665F] font-light text-lg">
            We'd love to hear from you
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white p-8 border border-[#E5E0D8]">
            <h2 className="font-serif text-2xl text-charcoal mb-6">Send us a Message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="text-xs uppercase tracking-wider text-charcoal mb-2 block">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b border-[#E5E0D8] px-0 py-3 focus:border-bronze focus:outline-none"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-charcoal mb-2 block">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b border-[#E5E0D8] px-0 py-3 focus:border-bronze focus:outline-none"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-charcoal mb-2 block">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b border-[#E5E0D8] px-0 py-3 focus:border-bronze focus:outline-none"
                  data-testid="contact-subject-input"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-charcoal mb-2 block">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full bg-transparent border border-[#E5E0D8] px-4 py-3 focus:border-bronze focus:outline-none resize-none"
                  data-testid="contact-message-input"
                ></textarea>
              </div>

              <button
                type="submit"
                className="inline-flex items-center justify-center bg-black text-white px-8 py-3 rounded-lg shadow-md hover:bg-gray-900 hover:shadow-lg transition-all duration-300 text-sm tracking-wide font-medium"
              data-testid="hero-cta-button"
              >
                Send Message
              </button>
            </form>
          </div>

          {/* Contact Info & FAQ */}
          <div className="space-y-8">
            {/* Contact Information */}
            <div className="bg-white p-8 border border-[#E5E0D8]">
              <h2 className="font-serif text-2xl text-charcoal mb-6">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <Mail className="text-bronze flex-shrink-0" size={24} />
                  <div>
                    <p className="text-sm font-medium text-charcoal mb-1">Email</p>
                    <a href="mailto:hello@siyaanish.com" className="text-[#6B665F] hover:text-bronze transition-colors">
                      info@siyaanish.com <br/> manisha@siyaanish.com
                    </a>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Phone className="text-bronze flex-shrink-0" size={24} />
                  <div>
                    <p className="text-sm font-medium text-charcoal mb-1">Phone</p>
                    <a href="tel:+919876543210" className="text-[#6B665F] hover:text-bronze transition-colors">
                      +91 79816 44655
                    </a>
                  </div>
                </div>

                <div className="flex gap-4">
                  <MapPin className="text-bronze flex-shrink-0" size={24} />
                  <div>
                    <p className="text-sm font-medium text-charcoal mb-1">Address</p>
                    <p className="text-[#6B665F]">
                      Hyderabad,<br />
                      India
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQs */}
            <div className="bg-beige p-8">
              <h2 className="font-serif text-2xl text-charcoal mb-6">Frequently Asked Questions</h2>
              
              <Accordion type="single" collapsible className="space-y-4" data-testid="faq-section">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="bg-white border border-[#E5E0D8] px-6">
                    <AccordionTrigger className="text-left font-serif text-base text-charcoal hover:text-bronze" data-testid={`faq-question-${index}`}>
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-[#6B665F] font-light leading-relaxed" data-testid={`faq-answer-${index}`}>
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
